import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import buildingSetUpsService from '../services/buildingSetUpsService';
import styles from '../assets/buildingSetUps.module.css';

/**
 * Building Set Ups.
 *
 * Rebuilt to the client's Sept 2026 layout. The important change is that most
 * columns are no longer typed in: each one is linked to a ticket and shows what
 * that ticket says. Staff only fill in a column when it has no linked ticket.
 *
 *   linked ticket completed          -> tick
 *   linked ticket open, booked in    -> the book-in date
 *   linked ticket open, not booked   -> "Awaiting booking"
 *   no linked ticket                 -> what staff set by hand, or blank
 *
 * "Mark as not met" is gone at the client's request: an outstanding
 * requirement is simply blank. "Clear" puts a cell back to blank.
 */

// The table, in the client's column order. `key` matches the backend column
// keys; `sort` is the sort_by value the API understands.
const TICKET_COLUMNS = [
    { key: 'keys', label: 'All Key Sets Ready', sort: 'keys', linkable: false },
    { key: 'bsu2', label: 'BSU 2', sort: 'bsu2', linkable: true },
    { key: '240V_certificate', label: 'EICR / MTVH 240v Certificate', sort: '240V_certificate', linkable: true },
    { key: 'gas_safe_certificate', label: 'Gas Safe Certificate', sort: 'gas_safe_certificate', linkable: true },
    { key: 'fire_risk_assessment', label: 'Fire Risk Assessment', sort: 'fire_risk_assessment', linkable: true },
    { key: 'legionella', label: 'Legionella Risk Assessment', sort: 'legionella', linkable: true },
    { key: 'fire_alarm', label: 'Fire Alarm Service', sort: 'fire_alarm', linkable: true },
    { key: 'emergency_lighting', label: 'Emergency Lighting Certificate', sort: 'emergency_lighting', linkable: true },
    { key: 'meter_debt', label: 'Meter Debt Cleared', sort: 'meter_debt', linkable: true },
    { key: 'clearance', label: 'Property Clearance', sort: 'clearance', linkable: true },
    // Kept on the client's instruction although it is absent from their layout.
    { key: 'asbestos_register', label: 'Asbestos Register', sort: 'asbestos_register', linkable: true },
];

const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB');
};

const BuildingSetUps = () => {
    const [entries, setEntries] = useState([]);
    const [stats, setStats] = useState({ total: 0, new: 0, in_progress: 0, completed: 0 });
    const [mappingWarning, setMappingWarning] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentStatus, setCurrentStatus] = useState('in_progress');
    const [searchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [sortBy, setSortBy] = useState('kpi_deadline');
    const [sortDir, setSortDir] = useState('asc');

    const [openDropdowns, setOpenDropdowns] = useState({});
    const [dropdownPositions, setDropdownPositions] = useState({});
    const [notesValues, setNotesValues] = useState({});
    const notesTimers = useRef({});

    // Add to BSU
    const [showAddModal, setShowAddModal] = useState(false);
    const [addSearch, setAddSearch] = useState('');
    const [addResults, setAddResults] = useState([]);
    const [addSearching, setAddSearching] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [addSubmitting, setAddSubmitting] = useState(false);
    const addSearchTimer = useRef(null);

    // Change linked ticket
    const [ticketPicker, setTicketPicker] = useState(null); // { entryId, column, label }
    const [ticketCandidates, setTicketCandidates] = useState([]);
    const [ticketLoading, setTicketLoading] = useState(false);

    const navigate = useNavigate();

    const openLinkedTicket = (ticketId) => {
        window.open(`/tickets/${ticketId}`, '_blank', 'noopener,noreferrer');
    };

    useEffect(() => {
        loadBuildingSetUps();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStatus, currentPage, itemsPerPage, searchTerm, sortBy, sortDir]);

    useEffect(() => {
        const onClickOutside = (event) => {
            if (!event.target.closest(`.${styles.requirement}`)) {
                setOpenDropdowns({});
            }
        };
        document.addEventListener('click', onClickOutside);
        return () => document.removeEventListener('click', onClickOutside);
    }, []);

    useEffect(() => {
        // Copied out of the ref so the cleanup clears the timers this effect saw.
        const timers = notesTimers.current;
        return () => {
            Object.values(timers).forEach((t) => t && clearTimeout(t));
        };
    }, []);

    const loadBuildingSetUps = async () => {
        try {
            setLoading(true);
            const response = await buildingSetUpsService.getBuildingSetUps({
                status: currentStatus,
                page: currentPage,
                limit: itemsPerPage,
                search: searchTerm,
                sort_by: sortBy,
                sort_dir: sortDir,
            });

            if (response.success) {
                setEntries(response.data || []);
                setStats(response.stats);
                setMappingWarning(response.mapping_warning || null);
                setTotalPages(response.pagination.total_pages);
                setTotalItems(response.pagination.total);
            } else {
                setEntries([]);
                setStats({ total: 0, new: 0, in_progress: 0, completed: 0 });
                setTotalPages(1);
                setTotalItems(0);
                toast.error(response.message || 'Failed to load building set ups');
            }
        } catch (error) {
            console.error('Error loading building set ups:', error);
            setEntries([]);
            toast.error('Failed to load building set ups');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusFilter = (status) => {
        setCurrentStatus(status);
        setCurrentPage(1);
    };

    const handleSort = (column) => {
        if (sortBy === column) {
            if (sortDir === 'asc') setSortDir('desc');
            else { setSortBy(''); setSortDir('asc'); }
        } else {
            setSortBy(column);
            setSortDir('asc');
        }
        setCurrentPage(1);
    };

    const sortableHeader = (label, column, extraClass = '') => {
        const active = sortBy === column;
        const arrow = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
        return (
            <th
                key={column}
                className={extraClass}
                onClick={() => handleSort(column)}
                title="Click to sort"
                style={{ cursor: 'pointer', userSelect: 'none' }}
            >
                {label}{arrow}
            </th>
        );
    };

    // --- Cells -------------------------------------------------------------

    const cellText = (cell) => {
        switch (cell?.state) {
            case 'met':
                return '✓';
            case 'scheduled':
                return formatDate(cell.date);
            case 'awaiting':
                return 'Awaiting booking';
            case 'not-applicable':
                return 'N/A';
            default:
                return '';
        }
    };

    const cellClass = (cell) => {
        switch (cell?.state) {
            case 'met':
                return styles.requirementMet;
            case 'scheduled':
                return styles.requirementInProgress;
            case 'awaiting':
                return styles.requirementAwaiting;
            default:
                return styles.requirementDefault;
        }
    };

    const toggleDropdown = (entryId, column, event) => {
        event.preventDefault();
        event.stopPropagation();

        const key = `${entryId}-${column}`;
        const isOpening = !openDropdowns[key];

        if (isOpening) {
            const rect = event.currentTarget.getBoundingClientRect();
            const dropUp = rect.bottom + 180 > window.innerHeight;
            setDropdownPositions((prev) => ({
                ...prev,
                [key]: {
                    top: dropUp ? 'auto' : rect.bottom + 2,
                    bottom: dropUp ? window.innerHeight - rect.top : 'auto',
                    right: window.innerWidth - rect.right,
                },
            }));
        }
        setOpenDropdowns((prev) => ({ ...prev, [key]: isOpening }));
    };

    const closeDropdown = (entryId, column) => {
        setOpenDropdowns((prev) => ({ ...prev, [`${entryId}-${column}`]: false }));
    };

    const updateRequirement = async (entryId, column, payload, successMessage) => {
        try {
            const response = await buildingSetUpsService.updateRequirement(entryId, column, payload);
            if (response.success) {
                toast.success(successMessage);
                loadBuildingSetUps();
            } else {
                toast.error(response.message || 'Failed to update');
            }
        } catch (error) {
            console.error('Error updating requirement:', error);
            toast.error('Failed to update');
        }
    };

    const handleMarkMet = (entryId, column, event) => {
        event.preventDefault();
        closeDropdown(entryId, column);
        updateRequirement(entryId, column, { status: 'met' }, 'Marked as met');
    };

    const handleMarkNotApplicable = (entryId, column, event) => {
        event.preventDefault();
        closeDropdown(entryId, column);
        updateRequirement(entryId, column, { status: 'not-applicable' }, 'Marked as not applicable');
    };

    // --- Linked tickets ----------------------------------------------------

    const openTicketPicker = async (entryId, column, label, event) => {
        event.preventDefault();
        closeDropdown(entryId, column);
        setTicketPicker({ entryId, column, label });
        setTicketLoading(true);
        const candidates = await buildingSetUpsService.getTicketCandidates(entryId, column);
        setTicketCandidates(candidates);
        setTicketLoading(false);
    };

    const applyTicketLink = async (body, message) => {
        if (!ticketPicker) return;
        const response = await buildingSetUpsService.setTicketLink(
            ticketPicker.entryId,
            ticketPicker.column,
            body
        );
        if (response.success) {
            toast.success(response.message || message);
            setTicketPicker(null);
            loadBuildingSetUps();
        } else {
            toast.error(response.message || 'Failed to update the linked ticket');
        }
    };

    const renderCell = (entry, column) => {
        const cell = entry.columns?.[column.key];
        if (!cell) return <div className={styles.requirement}>—</div>;

        const dropdownKey = `${entry.id}-${column.key}`;
        const isCompletedRound = entry.status === 'completed';
        const hasTicket = !!cell.ticket_id;
        const showMenu = !isCompletedRound;

        return (
            <div className={`${styles.requirement} ${cellClass(cell)}`}>
                {hasTicket ? (
                    <span
                        className={styles.ticketLinkedValue}
                        onClick={() => openLinkedTicket(cell.ticket_id)}
                        title={`From ticket #${cell.ticket_id} — click to open in a new tab`}
                    >
                        {cellText(cell) || '—'}
                    </span>
                ) : (
                    <span>{cellText(cell)}</span>
                )}

                {showMenu && (
                    <div className={styles.dropdown}>
                        <button
                            className={styles.dropdownToggle}
                            type="button"
                            onClick={(e) => toggleDropdown(entry.id, column.key, e)}
                            aria-haspopup="true"
                            aria-expanded={!!openDropdowns[dropdownKey]}
                        >
                            <span className={styles.glyphicon}>▼</span>
                        </button>
                        <div
                            className={`${styles.dropdownMenu} ${openDropdowns[dropdownKey] ? styles.show : ''}`}
                            style={dropdownPositions[dropdownKey] ? {
                                top: dropdownPositions[dropdownKey].top === 'auto' ? 'auto' : `${dropdownPositions[dropdownKey].top}px`,
                                bottom: dropdownPositions[dropdownKey].bottom === 'auto' ? 'auto' : `${dropdownPositions[dropdownKey].bottom}px`,
                                right: `${dropdownPositions[dropdownKey].right}px`,
                            } : {}}
                        >
                            {hasTicket ? (
                                <>
                                    <button className={styles.dropdownItem} onClick={() => openLinkedTicket(cell.ticket_id)}>
                                        Open ticket #{cell.ticket_id}
                                    </button>
                                    {column.linkable && (
                                        <button
                                            className={styles.dropdownItem}
                                            onClick={(e) => openTicketPicker(entry.id, column.key, column.label, e)}
                                        >
                                            Change linked ticket…
                                        </button>
                                    )}
                                </>
                            ) : (
                                <>
                                    <button className={styles.dropdownItem} onClick={(e) => handleMarkMet(entry.id, column.key, e)}>
                                        Mark as met
                                    </button>
                                    <button className={styles.dropdownItem} onClick={(e) => handleMarkNotApplicable(entry.id, column.key, e)}>
                                        Mark as not applicable
                                    </button>
                                    {column.linkable && (
                                        <button
                                            className={styles.dropdownItem}
                                            onClick={(e) => openTicketPicker(entry.id, column.key, column.label, e)}
                                        >
                                            {cell.ticket_source === 'unlinked' ? 'Link a ticket again…' : 'Link a ticket…'}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // --- Row-level actions -------------------------------------------------

    const getOutstandingColumns = (entry) =>
        Object.values(entry.columns || {})
            .filter((cell) => cell.state !== 'met' && cell.state !== 'not-applicable')
            .map((cell) => cell.label);

    const handleUpdateEntryStatus = async (entry, newStatus) => {
        try {
            const updatePayload = { status: newStatus };

            if (newStatus === 'completed') {
                const missingColumns = getOutstandingColumns(entry);
                if (missingColumns.length > 0) {
                    const missingList = missingColumns.join('\n- ');
                    const confirmed = window.confirm(
                        `Are you sure you want to make this property live?\n\n` +
                        `The following items are still outstanding:\n- ${missingList}\n\n` +
                        `Click OK to make the property live anyway, or Cancel to return to the Building Set Ups page.`
                    );
                    if (!confirmed) {
                        toast.info('Make live cancelled');
                        return;
                    }
                    updatePayload.force_live = true;
                }
            }

            const response = await buildingSetUpsService.updateEntry(entry.id, updatePayload);
            if (response.success) {
                toast.success('Status updated');
                loadBuildingSetUps();
            } else {
                toast.error(response.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status');
        }
    };

    const handleNotesChange = (entryId, value) => {
        setNotesValues((prev) => ({ ...prev, [entryId]: value }));
        if (notesTimers.current[entryId]) clearTimeout(notesTimers.current[entryId]);
        notesTimers.current[entryId] = setTimeout(async () => {
            const response = await buildingSetUpsService.updateEntry(entryId, { notes: value });
            if (!response.success) toast.error('Failed to save notes');
            delete notesTimers.current[entryId];
        }, 800);
    };

    const rowClassName = (entry) => {
        let className = styles.tableRow;
        if (entry.status === 'completed') return `${className} ${styles.completed}`;
        if (entry.isOverdue) className += ` ${styles.pastDue}`;
        else if (entry.isDueSoon) className += ` ${styles.dueSoon}`;
        return className;
    };

    // --- Add to BSU --------------------------------------------------------

    const openAddModal = () => {
        setSelectedProperty(null);
        setAddSearch('');
        setAddResults([]);
        setShowAddModal(true);
    };

    const handleAddSearchChange = (value) => {
        setAddSearch(value);
        setSelectedProperty(null);
        if (addSearchTimer.current) clearTimeout(addSearchTimer.current);
        const q = value.trim();
        if (q.length < 2) {
            setAddResults([]);
            setAddSearching(false);
            return;
        }
        setAddSearching(true);
        addSearchTimer.current = setTimeout(async () => {
            setAddResults(await buildingSetUpsService.searchProperties(q));
            setAddSearching(false);
        }, 350);
    };

    const submitAdd = async (reopen = false) => {
        if (!selectedProperty) return;
        setAddSubmitting(true);
        try {
            const res = await buildingSetUpsService.addEntry(selectedProperty.id, reopen);
            if (res.httpStatus === 409 && res.code === 'already_completed') {
                const when = res.completed_at ? ` on ${new Date(res.completed_at).toLocaleDateString()}` : '';
                if (window.confirm(
                    `"${selectedProperty.name}" has already completed Building Set Up${when}.\n\n` +
                    'Start a new round? The table is cleared and the current round is kept in the property history.'
                )) {
                    await submitAdd(true);
                }
                return;
            }
            if (res.success) {
                toast.success(res.message || 'Property added to Building Set Up.');
                setShowAddModal(false);
                setCurrentStatus(res.data?.status === 'in-progress' ? 'in_progress' : (res.data?.status || 'new'));
                setCurrentPage(1);
                loadBuildingSetUps();
            } else {
                toast.error(res.message || 'Failed to add property to Building Set Up.');
            }
        } finally {
            setAddSubmitting(false);
        }
    };

    const handleExportCsv = async () => {
        try {
            await buildingSetUpsService.exportCsv({
                status: currentStatus,
                search: searchTerm,
                sort_by: sortBy,
                sort_dir: sortDir,
            });
            toast.success('CSV export completed');
        } catch (error) {
            toast.error('Error exporting CSV');
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.breadcrumb}>
                <span onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
                <span className={styles.breadcrumbSeparator}>/</span>
                <span onClick={() => navigate('/tickets')} style={{ cursor: 'pointer', color: '#337ab7' }}>Tickets</span>
                <span className={styles.breadcrumbSeparator}>/</span>
                <span>Building Set Ups</span>
            </div>

            {/* Overview — compact, so the table sits higher on the page */}
            <div className={styles.overviewBlock}>
                <div className={styles.statusOverview}>
                    <div className={styles.statusCard} onClick={() => handleStatusFilter('all')}>
                        <div className={styles.statusNumber}>{stats.total}</div>
                        <div className={styles.statusLabel}>All</div>
                    </div>
                    <div className={styles.statusCard} onClick={() => handleStatusFilter('new')}>
                        <div className={styles.statusNumber}>{stats.new}</div>
                        <div className={styles.statusLabel}>New</div>
                    </div>
                    <div className={styles.statusCard} onClick={() => handleStatusFilter('in_progress')}>
                        <div className={styles.statusNumber}>{stats.in_progress}</div>
                        <div className={styles.statusLabel}>In Progress</div>
                    </div>
                    <div className={styles.statusCard} onClick={() => handleStatusFilter('completed')}>
                        <div className={styles.statusNumber}>{stats.completed}</div>
                        <div className={styles.statusLabel}>Completed</div>
                    </div>
                </div>
            </div>

            {mappingWarning && (
                <div className={styles.mappingWarning}>{mappingWarning}</div>
            )}

            <div className={styles.contentBlock}>
                <div className={styles.contentHeader}>
                    <h3 className={styles.contentTitle}>Building set ups</h3>
                    <div className={styles.contentActions}>
                        <button className={styles.addButton} onClick={openAddModal}>+ Add to BSU</button>
                        <button className={styles.exportButton} onClick={handleExportCsv}>Export to CSV</button>
                        <button className={styles.printButton} onClick={() => window.print()}>Print</button>
                    </div>
                </div>

                <div className={styles.tableContainer}>
                    {loading ? (
                        <div className={styles.loading}>Loading building set ups...</div>
                    ) : (
                        <table className={`${styles.dataTable} ${styles.compactTable}`}>
                            <thead>
                                <tr>
                                    {sortableHeader('Property address and ID', 'property')}
                                    {sortableHeader('KPI Deadline', 'kpi_deadline')}
                                    {sortableHeader('Days taken', 'days_taken', styles.numericHeader)}
                                    {sortableHeader('Status', 'status')}
                                    {sortableHeader('Building Set-up Start Date', 'set_up_start_date')}
                                    <th>Assigned Contractor</th>
                                    <th>Building Set-up Complete Date</th>
                                    {TICKET_COLUMNS.map((c) => sortableHeader(c.label, c.sort))}
                                    <th>Comments</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((entry) => (
                                    <tr key={entry.id} className={rowClassName(entry)}>
                                        <td className={styles.propertyCell}>
                                            <a
                                                className={styles.propertyName}
                                                href={`/properties/${entry.property?.id}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                title="Open property page"
                                            >
                                                {entry.property?.name || '—'}
                                            </a>
                                            <div className={styles.propertyId}>{entry.property?.property_id || ''}</div>
                                        </td>
                                        <td className={styles.kpiCell}>{formatDate(entry.kpi_deadline) || '—'}</td>
                                        <td className={styles.daysCell}>
                                            {entry.days_taken !== null && entry.days_taken !== undefined ? entry.days_taken : '—'}
                                        </td>
                                        <td>
                                            <span className={
                                                entry.status === 'completed' ? styles.statusCompleted
                                                    : entry.status === 'new' ? styles.statusNew
                                                        : styles.statusInProgress
                                            }>
                                                {String(entry.status || '').replace('-', ' ')}
                                            </span>
                                        </td>
                                        <td>
                                            {entry.bsu_ticket_id ? (
                                                <span
                                                    className={styles.ticketLinkedValue}
                                                    onClick={() => openLinkedTicket(entry.bsu_ticket_id)}
                                                    title={`From ticket #${entry.bsu_ticket_id} — click to open in a new tab`}
                                                >
                                                    {formatDate(entry.set_up_start_date) || '—'}
                                                </span>
                                            ) : (
                                                formatDate(entry.set_up_start_date) || '—'
                                            )}
                                        </td>
                                        <td>{entry.assigned_contractor || '—'}</td>
                                        <td title="Filled in automatically when the linked Building Set Up ticket is completed">
                                            {formatDate(entry.set_up_complete_date) || '—'}
                                        </td>
                                        {TICKET_COLUMNS.map((column) => (
                                            <td key={column.key}>{renderCell(entry, column)}</td>
                                        ))}
                                        <td className={styles.notesCell}>
                                            <textarea
                                                id={`notes-${entry.id}`}
                                                rows={4}
                                                className={styles.notesInput}
                                                value={notesValues[entry.id] !== undefined ? notesValues[entry.id] : (entry.notes || '')}
                                                onChange={(e) => handleNotesChange(entry.id, e.target.value)}
                                                placeholder="Add notes..."
                                                disabled={entry.status === 'completed'}
                                            />
                                            {entry.notes_updated_by_name && entry.notes_updated_at && (
                                                <div className={styles.notesMeta}>
                                                    By {entry.notes_updated_by_name} · {formatDate(entry.notes_updated_at)}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div className={styles.actionButtons}>
                                                <button
                                                    className={styles.actionButton}
                                                    onClick={() => handleUpdateEntryStatus(entry, 'in-progress')}
                                                    disabled={entry.status === 'in-progress' || entry.status === 'completed'}
                                                >
                                                    Mark as In Progress
                                                </button>
                                                <button
                                                    className={`${styles.actionButton} ${styles.makeLiveButton}`}
                                                    onClick={() => handleUpdateEntryStatus(entry, 'completed')}
                                                    disabled={entry.status === 'completed'}
                                                >
                                                    Make live
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {!loading && totalItems > 0 && (
                    <div className={styles.paginationContainer}>
                        <div className={styles.paginationInfo}>
                            {itemsPerPage >= 999999
                                ? `Showing all ${totalItems} entries`
                                : `Showing ${((currentPage - 1) * itemsPerPage) + 1} to ${Math.min(currentPage * itemsPerPage, totalItems)} of ${totalItems} entries`}
                        </div>
                        <div className={styles.paginationControls}>
                            <div className={styles.paginationEntries}>
                                <label htmlFor="bsu-per-page">Show:</label>
                                <select
                                    id="bsu-per-page"
                                    value={itemsPerPage >= 999999 ? 999999 : itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(e.target.value === 'all' ? 999999 : Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className={styles.entriesSelect}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value="all">All</option>
                                </select>
                                <span>entries</span>
                            </div>
                            <div className={styles.paginationButtons}>
                                <button
                                    className={styles.paginationButton}
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                <div className={styles.pageNumbers}>
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) pageNum = i + 1;
                                        else if (currentPage <= 3) pageNum = i + 1;
                                        else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                        else pageNum = currentPage - 2 + i;
                                        return (
                                            <button
                                                key={pageNum}
                                                className={`${styles.pageNumber} ${currentPage === pageNum ? styles.active : ''}`}
                                                onClick={() => setCurrentPage(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button
                                    className={styles.paginationButton}
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Add to BSU */}
            {showAddModal && (
                <div className={styles.modalOverlay} onClick={() => !addSubmitting && setShowAddModal(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h4 className={styles.modalTitle}>Add property to Building Set Up</h4>
                            <button className={styles.modalClose} onClick={() => setShowAddModal(false)} aria-label="Close">×</button>
                        </div>
                        <div className={styles.modalBody}>
                            <input
                                type="text"
                                id="bsu-add-search"
                                className={styles.searchInput}
                                placeholder="Search by property name, ID or address…"
                                value={addSearch}
                                onChange={(e) => handleAddSearchChange(e.target.value)}
                                autoFocus
                            />
                            <p className={styles.modalHint}>
                                Building Set Up is tracked per property. Selecting a property adds it (and any of its maintenance rooms) to the module.
                            </p>
                            {addSearching && <p className={styles.modalHint}>Searching…</p>}
                            {!addSearching && addSearch.trim().length >= 2 && addResults.length === 0 && (
                                <p className={styles.modalHint}>No matching properties found.</p>
                            )}
                            {addResults.length > 0 && (
                                <ul className={styles.resultList}>
                                    {addResults.map((p) => (
                                        <li
                                            key={p.id}
                                            className={`${styles.resultItem} ${selectedProperty?.id === p.id ? styles.resultItemSelected : ''}`}
                                            onClick={() => setSelectedProperty(p)}
                                        >
                                            <div className={styles.resultName}>{p.name || `Property #${p.id}`}</div>
                                            <div className={styles.resultMeta}>
                                                {[p.property_id, p.address_1, p.postcode].filter(Boolean).join(' · ')}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.secondaryBtn} onClick={() => setShowAddModal(false)} disabled={addSubmitting}>Cancel</button>
                            <button className={styles.primaryBtn} onClick={() => submitAdd(false)} disabled={!selectedProperty || addSubmitting}>
                                {addSubmitting ? 'Adding…' : 'Add to BSU'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Change linked ticket */}
            {ticketPicker && (
                <div className={styles.modalOverlay} onClick={() => setTicketPicker(null)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h4 className={styles.modalTitle}>Linked ticket — {ticketPicker.label}</h4>
                            <button className={styles.modalClose} onClick={() => setTicketPicker(null)} aria-label="Close">×</button>
                        </div>
                        <div className={styles.modalBody}>
                            <p className={styles.modalHint}>
                                This column normally follows the most recent matching ticket raised in this
                                Building Set Up round. Certificate renewals are not picked up automatically,
                                but you can choose one here. Pick a different ticket if the automatic one is
                                not right.
                            </p>
                            {ticketLoading && <p className={styles.modalHint}>Loading tickets…</p>}
                            {!ticketLoading && ticketCandidates.length === 0 && (
                                <p className={styles.modalHint}>No tickets of this type exist for the property.</p>
                            )}
                            {ticketCandidates.length > 0 && (
                                <ul className={styles.resultList}>
                                    {ticketCandidates.map((t) => (
                                        <li
                                            key={t.id}
                                            className={styles.resultItem}
                                            onClick={() => applyTicketLink({ ticket_id: t.id }, 'Ticket linked.')}
                                        >
                                            <div className={styles.resultName}>#{t.id} — {t.name}</div>
                                            <div className={styles.resultMeta}>
                                                {[
                                                    // Renewals are never linked automatically, so say
                                                    // so plainly when one is offered here.
                                                    t.is_certificate_renewal ? 'Certificate renewal' : null,
                                                    t.date_completed ? `Completed ${formatDate(t.date_completed)}` : null,
                                                    t.book_in_date ? `Booked in ${formatDate(t.book_in_date)}` : null,
                                                    t.contractor_name,
                                                    `Raised ${formatDate(t.created_at)}`,
                                                ].filter(Boolean).join(' · ')}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                className={styles.secondaryBtn}
                                onClick={() => applyTicketLink({ reset: true }, 'Back to the automatic match.')}
                            >
                                Use automatic match
                            </button>
                            <button
                                className={styles.secondaryBtn}
                                onClick={() => applyTicketLink({ ticket_id: null }, 'Ticket unlinked.')}
                            >
                                Unlink — set by hand
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BuildingSetUps;
